import request from "../helpers/request";
import { baseUrl, HTTP_STATUS_CODES as SERVER_STATUS_CODES } from "../helpers/http";
import { getLeadingQueryStringChar } from "../helpers/url";
import AuthService from "./auth-service";

import type { ApiResponse } from "../interfaces";

type RequestMethods = "GET"|"POST"|"PUT"|"DELETE";
type RequestParams<PARAMS> = PARAMS & RequestInit;
interface APIRequest<PARAMS, PAYLOAD> { 
  method: RequestMethods; 
  endpoint: string; 
  params?: PARAMS; 
  payload?: PAYLOAD;
  fetchOptions?: RequestInit;
}

// Complete list of standard RequestInit properties
const REQUEST_INIT_KEYS = [
  //"method",
  //"headers",
  //"body",
  "mode",
  //"credentials",
  "cache",
  "redirect",
  "referrer",
  "referrerPolicy",
  "integrity",
  "keepalive",
  "signal",
  "priority", // Extended by some environments
  "duplex",   // Required for streaming bodies in node/fetch
  "window"    // Historical/specific implementations
];

/**
 * Extracts RequestInit properties from a mixed object.
 */
function extractRequestInit(sourceObject): RequestInit {
  const result = {};

  if(!sourceObject || typeof sourceObject !== "object") {
    return result;
  }

  for(const key of REQUEST_INIT_KEYS) {
    if(key in sourceObject && typeof sourceObject[key] !== "undefined") {
      result[key] = sourceObject[key];
    }
  }

  return result as RequestInit;
}

function extractNonRequestInit<PARAMS>(sourceObject): PARAMS {
  const result = {};

  if(!sourceObject || typeof sourceObject !== "object") {
    return result as PARAMS;
  }

  for(const key of Object.keys(sourceObject)) {
    if(!(REQUEST_INIT_KEYS.includes(key)) && typeof sourceObject[key] !== "undefined") {
      result[key] = sourceObject[key];
    }
  }

  return result as PARAMS;
}

export default class ApiService {
  static async get<PARAMS>(endpoint: string, params?: RequestParams<PARAMS>): Promise<ApiResponse> { 
    const fetchOptions: RequestInit = extractRequestInit(params);
    const requestParams: PARAMS = extractNonRequestInit<PARAMS>(params);

    return await ApiService.#makeRequest({ 
      method: "GET", 
      endpoint, 
      params: requestParams, 
      fetchOptions
    });
  }

  static async post<PARAMS, PAYLOAD>(endpoint: string, params?: RequestParams<PARAMS>, payload?: PAYLOAD): Promise<ApiResponse> { 
    const fetchOptions: RequestInit = extractRequestInit(params);
    const requestParams: PARAMS = extractNonRequestInit<PARAMS>(params);
  
    return await ApiService.#makeRequest({ 
      method: "POST", 
      endpoint, 
      params: requestParams, 
      fetchOptions, 
      payload
    });
  }

  static async put<PARAMS, PAYLOAD>(endpoint: string, params?: RequestParams<PARAMS>, payload?: PAYLOAD): Promise<ApiResponse> { 
    const fetchOptions: RequestInit = extractRequestInit(params);
    const requestParams: PARAMS = extractNonRequestInit<PARAMS>(params);

    return await ApiService.#makeRequest({ 
      method: "PUT", 
      endpoint, 
      params: requestParams, 
      fetchOptions, 
      payload
    });
  }

  static async delete<PARAMS, PAYLOAD>(endpoint: string, params?: RequestParams<PARAMS>, payload?: PAYLOAD): Promise<ApiResponse> { 
    const fetchOptions: RequestInit = extractRequestInit(params);
    const requestParams: PARAMS = extractNonRequestInit<PARAMS>(params);

    return await ApiService.#makeRequest({ 
      method: "DELETE", 
      endpoint, 
      params: requestParams, 
      fetchOptions, 
      payload 
    });
  }

  /**
   * @method request - Make http requests to remote urls
   *
   * @param {String} method: The request method.
   * @param {String} endpoint: The request endpoint (allows placeholders)
   *    e.g: "posts/:postId"
   * @param {Object} params: Key-value pairs of parameters to replace in the endpoint.
   *    e.g: {
   *      postId: 5, // Will replace :postId with 5 in the endpointwhen post is made
   *      name: "James" // will be appended to query string, since no matching placeholder exists in endpoint
   *    }
   *
   * @param {Object} payload: The request payload
   *
   * @return {Object}
   *
   */
  //static async #makeRequest<PARAMS, PAYLOAD>(method: RequestMethods, endpoint: string, params?: PARAMS, payload?: PAYLOAD): Promise<ApiResponse> {
  static async #makeRequest<PARAMS, PAYLOAD>(options: APIRequest<PARAMS, PAYLOAD>): Promise<ApiResponse> { 
    const { method, params, payload, fetchOptions = {} } = options;

    let { endpoint } = options;

    // Replace all named parameters in the URL
    Object.keys(params ?? {}).forEach(key => {
      if(endpoint.indexOf(`:${key}`) !== -1) {
        endpoint = endpoint.replace(`:${key}`, encodeURIComponent(params?.[key]));

        delete params?.[key];
      }
    });

    // If it's a GET request, append all other params in the query string.
    if(method === "GET") {
      const queryParams: string[] = [];

      Object.keys(params ?? {}).forEach(key => queryParams.push(
        `${key}=${encodeURIComponent(params?.[key])}`
      ));

      endpoint += (queryParams.length
        ? `${getLeadingQueryStringChar(endpoint)}${queryParams.join("&")}`
        : ""
      );
    }

    
    const url = [baseUrl, endpoint].join(endpoint.charAt(0) === "/" ? "" : "/");

    const frontendRoute = window.location.pathname;
    const isProtectedClientRoute = AuthService.isProtectedRoute(frontendRoute);

    if(isProtectedClientRoute && AuthService.isProtectedRoute(endpoint)) {
      if(!AuthService.isAuthenticatedUser()) {
        AuthService.redirect(frontendRoute);
        return { 
          error: true, 
          statusCode: SERVER_STATUS_CODES.UNAUTHORIZED, 
          errorData: { message: "Authentication required" } 
        };
      }
    }

    const response =  await request[method](url, payload, fetchOptions);
    const result = await response?.json();

    if(response?.status === SERVER_STATUS_CODES.UNAUTHORIZED && result?.errorData?.message === "Invalid access token.") {
      // Prevent our components from seeing the user as logged in.
      AuthService.deleteAccessToken();

      // Prevent infinite-loop redirection.
      // This is in conjunction with some updates to the
      // useBusinessNotCreateNotifiction hook.
      // Also, it makes sense that if the user is currently
      // accessing or on a non-protected page,
      // we don't need to redirect them to the login page to be authenticated.
      if(isProtectedClientRoute) {
        AuthService.redirect(frontendRoute);
      }
    }

    if(response?.status >= SERVER_STATUS_CODES.SERVER_ERROR) {
      return {
        error: true, 
        statusCode: response.status,
        errorData: {
          message: "A server error has occured. Please try again later.",
        }
      };
    }

    return { ...result, statusCode: response?.status };
  }
}
